import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Checkbox,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import {
  CustomTreeItem,
  TypographyNormal,
  TypographySmall,
} from "../../../customComponent";
import { SET_ADS_PLACEHOLDER_FLAG } from "../../../../store/actionTypes/cmsActionType";
import { bplrtOptions } from "../../../../externalFeatures/components/bplrt";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import {
  GroupFolderTooltipIcon,
  GroupFolderOpenTooltipIcon,
} from "../../../SharedComponents";

const LayerAsset = ({
  layerID,
  content,
  index,
  assetCount,
  groupChildIndex,
}) => {
  const dispatch = useDispatch();
  const { adsPlaceholderFlag } = useSelector((state) => state.cms);

  const { t } = useTranslation("cms");

  const contentAdTrue = useMemo(() => {
    switch (content.type) {
      case "VISTAR_ADVERTISEMENT":
      case "HIVESTACK_ADVERTISEMENT":
        return true;

      default:
        return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const showContentDuration = useMemo(() => {
    if (contentAdTrue) {
      return true;
    }

    switch (content.type) {
      case "GROUPED":
      case "VISTAR_ADVERTISEMENT":
      case "HIVESTACK_ADVERTISEMENT":
        return true;

      default:
        return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const contentName = useMemo(() => {
    if (contentAdTrue) {
      const adType = content.type.split("_")[0];
      return t(`adContent.name${adType}`);
    }

    switch (content.type) {
      case "BPLRT_EQUIPMENT":
      case "BPLRT_EQUIPMENT_TABLE":
      case "BPLRT_SCHEDULE":
      case "BPLRT_SERVICE_INFO":
      case "BPLRT_SMP":
        const appType = content.type;
        return t(`bplrt.component${bplrtOptions[appType].label}`);

      case "GROUPED":
        return content.name;

      default:
        return content.name;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const togglePlaceholderFlag = (index) => {
    const updated = [];
    adsPlaceholderFlag[layerID].forEach((flag, i) => {
      updated.push(i !== index ? flag : !flag);
    });
    dispatch({
      type: SET_ADS_PLACEHOLDER_FLAG,
      payload: {
        id: layerID,
        edit: updated,
      },
    });
  };

  if (content.type === "BPLRT_TICKER") {
    return <></>;
  }

  return (
    <Box
      width={"468px"}
      key={"content" + index}
      sx={{
        mt: 1,
        pr: 1,
        borderRadius: "4px",
        ":hover": {
          backgroundColor: "rgba(28, 78, 216, 0.1)",
        },
      }}
      display={"flex"}
      justifyContent={"space-between"}
      alignItems={"center"}
    >
      <Box
        width={"333px"}
        display={"flex"}
        alignItems={"center"}
        height={content.type.includes("_ADVERTISEMENT") && "55px"}
      >
        <Box sx={{ width: "33px", textAlign: "right" }}>{`${index + 1}.`}</Box>
        <Box display={"flex"} flexDirection={"column"} sx={{ ml: 1 }}>
          {content?.type !== "GROUPED" ? (
            <Tooltip arrow disableInteractive title={contentName}>
              <Box sx={{ maxWidth: "275px" }}>
                <TypographyNormal
                  sx={{
                    fontSize: "15px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {contentName}
                </TypographyNormal>
              </Box>
            </Tooltip>
          ) : (
            <SimpleTreeView disableSelection>
              <CustomTreeItem
                itemId={`${content.assetID}`}
                ExpandIcon={GroupFolderTooltipIcon}
                CollapseIcon={GroupFolderOpenTooltipIcon}
                customRootStyle={{
                  sx: {
                    maxWidth: "275px",
                    position: "relative",
                  },
                }}
                label={
                  <Tooltip arrow disableInteractive title={contentName}>
                    <Box sx={{ maxWidth: "270px" }}>
                      <TypographyNormal
                        sx={{
                          fontSize: "15px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {contentName}
                      </TypographyNormal>
                    </Box>
                  </Tooltip>
                }
              >
                {content.children?.length > 0 &&
                  content.children.map((child, i) => (
                    <CustomTreeItem
                      key={`${child.id}`}
                      itemId={`${child.id}`}
                      customRootStyle={{
                        sx: {
                          maxWidth: "275px",
                          position: "relative",
                        },
                      }}
                      customContentStyle={{
                        sx: {
                          pl: "16px",
                          ml: "16px",
                          backgroundColor:
                            groupChildIndex === i
                              ? "rgba(177, 255, 88, 0.5)"
                              : "unset",
                          cursor: "unset",
                          ":hover": {
                            backgroundColor:
                              groupChildIndex === i
                                ? "rgba(177, 255, 88, 0.5)"
                                : "unset",
                          },
                        },
                      }}
                      label={
                        <Tooltip arrow disableInteractive title={child.name}>
                          <Box sx={{ maxWidth: "270px" }}>
                            <TypographyNormal
                              sx={{
                                fontSize: "15px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {child.name}
                            </TypographyNormal>
                          </Box>
                        </Tooltip>
                      }
                    />
                  ))}
              </CustomTreeItem>
            </SimpleTreeView>
          )}

          {content.placeholder && (
            <Box display={"flex"} alignItems={"center"}>
              <TypographySmall>
                {t("detail.contentAdShowPlaceholder")}
              </TypographySmall>
              <Checkbox
                size="small"
                checked={adsPlaceholderFlag[layerID]?.[index]}
                onChange={() => togglePlaceholderFlag(index)}
                sx={{
                  paddingY: 0,
                  paddingX: 1,
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Box display={"flex"} alignItems={"center"}>
        {content.isMuted && (
          <Tooltip
            arrow
            disableInteractive
            title={t("detail.contentVideoMutedTip")}
          >
            <Box display={"flex"} alignItems={"center"} sx={{ mr: 1 }}>
              <VolumeOffIcon />
            </Box>
          </Tooltip>
        )}

        {(assetCount > 1 || showContentDuration) && (
          <Tooltip
            arrow
            disableInteractive
            title={t("detail.contentDurationTip")}
          >
            <TextField
              size="small"
              placeholder="0"
              defaultValue={content.interval}
              sx={{ width: "100px" }}
              inputProps={{ style: { cursor: "default", fontSize: 15 } }}
              InputProps={{
                readOnly: true,
                sx: { cursor: "default" },
                endAdornment: (
                  <InputAdornment
                    position="end"
                    disableTypography
                    sx={{ fontSize: 14 }}
                  >
                    {t("detail.contentDurationSuffix")}
                  </InputAdornment>
                ),
              }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default LayerAsset;
